import { omit } from 'lodash'
import React, { useEffect } from 'react'

import useIsClient from '../hooks/useIsClient'
import { PlainProduct } from '../hooks/useProducts'
import { DeviceType, IndustryOrMaterial, TypeOfTesting } from '../logic/catalog'
import { gaEvents } from '../logic/ga4'
import { ProductFiltersByType } from '../logic/productFiltering'

import CategoryMenu from './CategoryMenu'
import { ProductListing } from './product/ProductListing'
import ProductListingSkeleton from './product/ProductListingSkeleton'
import { ProductFiltersProps } from './ProductFilters'

import './FilterableProductList.scss'

export interface FilterableProductListProps {
  availableFilters: ProductFiltersProps['availableFilters']
  deviceTypes: DeviceType[]
  filters: ProductFiltersByType
  onClear: () => void
  onFilterChange: ProductFiltersProps['onChange']
  products: PlainProduct[]
  industryOrMaterialValues: IndustryOrMaterial[]
  typeOfTestingValues: TypeOfTesting[]
}

const Listing = ({
  filters,
  onFilterChange,
  products,
  industryOrMaterialValues,
  typeOfTestingValues,
}: Pick<
  FilterableProductListProps,
  | 'filters'
  | 'onFilterChange'
  | 'products'
  | 'industryOrMaterialValues'
  | 'typeOfTestingValues'
>) => {
  const isClient = useIsClient()

  const hideProductList =
    filters.text.value.length > 0 ||
    Object.values(omit(filters, 'text')).some(filter => filter.value !== null)

  useEffect(() => {
    if (!hideProductList && products.length > 0) {
      gaEvents.productsShown('Product catalog', products.slice(0, 20))
    }
  }, [hideProductList, products])

  if (!isClient) {
    return (
      <>
        <ProductListingSkeleton />
        {hideProductList && (
          <ProductListing
            filters={filters}
            onFilterChange={onFilterChange}
            products={products}
            className="productListing--ssr"
          />
        )}
        <CategoryMenu
          industryOrMaterialValues={industryOrMaterialValues}
          typeOfTestingValues={typeOfTestingValues}
        />
      </>
    )
  }
  return (
    <>
      {hideProductList && (
        <ProductListing
          filters={filters}
          onFilterChange={onFilterChange}
          products={products}
        />
      )}
      <CategoryMenu
        industryOrMaterialValues={industryOrMaterialValues}
        typeOfTestingValues={typeOfTestingValues}
      />
    </>
  )
}

const FilterableProductList = ({
  filters,
  onFilterChange,
  products,
  industryOrMaterialValues,
  typeOfTestingValues,
}: Pick<
  FilterableProductListProps,
  | 'filters'
  | 'onFilterChange'
  | 'products'
  | 'industryOrMaterialValues'
  | 'typeOfTestingValues'
>) => {
  return (
    <div className="filterableProductList">
      <Listing
        filters={filters}
        products={products}
        onFilterChange={onFilterChange}
        industryOrMaterialValues={industryOrMaterialValues}
        typeOfTestingValues={typeOfTestingValues}
      />
    </div>
  )
}

export default FilterableProductList
